import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { USDZExporter } from "three/examples/jsm/exporters/USDZExporter.js";
import * as THREE from "three";

/**
 * Convert an optimized GLB file to USDZ format for iOS AR Quick Look.
 *
 * Uses Three.js to load the GLB into a scene, then exports via
 * USDZExporter. Runs entirely in the browser — no server needed.
 */
export async function convertToUsdz(
  glbFile: File,
  onProgress?: (step: string) => void
): Promise<File> {
  onProgress?.("Loading model for USDZ conversion...");

  const arrayBuffer = await glbFile.arrayBuffer();

  // Load GLB into a Three.js scene
  const loader = new GLTFLoader();
  const gltf = await new Promise<{ scene: THREE.Group }>((resolve, reject) => {
    loader.parse(
      arrayBuffer,
      "",
      (result) => resolve(result as { scene: THREE.Group }),
      (err) => reject(err)
    );
  });

  const scene = gltf.scene;

  // Ensure the scene has some light so the USDZ doesn't render dark
  // in Quick Look (Quick Look has its own lighting but the export
  // benefits from baked light direction hints)
  scene.traverse((child) => {
    if ((child as THREE.Mesh).isMesh) {
      const mesh = child as THREE.Mesh;
      if (mesh.material) {
        const materials = Array.isArray(mesh.material)
          ? mesh.material
          : [mesh.material];
        for (const mat of materials) {
          // Ensure materials are double-sided for USDZ
          mat.side = THREE.DoubleSide;
        }
      }
    }
  });

  onProgress?.("Exporting to USDZ...");

  const exporter = new USDZExporter();
  const usdzArrayBuffer = await exporter.parse(scene);

  const baseName = glbFile.name.replace(/\.glb$/i, "");
  const usdzFile = new File([usdzArrayBuffer], `${baseName}.usdz`, {
    type: "model/vnd.usdz+zip",
  });

  const sizeMB = (usdzFile.size / 1024 / 1024).toFixed(1);
  onProgress?.(`USDZ created — ${sizeMB} MB`);

  return usdzFile;
}
